import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Globe, PencilSimple, Check, X, LinkSimple, Info } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminGetDomain, adminPutDomain } from "@/lib/api";

/**
 * <YourDomainBanner /> — a top-of-page card on the Super Admin Console
 * that shows and lets the super-admin edit the "Public Domain" — the URL
 * embedded in every outbound email, PDF footer, and OG canonical.
 *
 * Design intent
 * ─────────────
 * · Sits above the tabs, in the same brand-blue tint as the console header.
 * · Always shows the *resolved* URL (DB override → env → default) so admins
 *   never wonder which one is winning.
 * · Inline edit with clear "save" / "cancel" — no modal, no page navigation.
 * · Shows the source (DB / env / default) as a small badge so misconfig is
 *   obvious at a glance.
 */
export default function YourDomainBanner() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-domain"],
    queryFn: adminGetDomain,
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (data?.url) setDraft(data.url);
  }, [data?.url]);

  const save = useMutation({
    mutationFn: adminPutDomain,
    onSuccess: (fresh) => {
      qc.setQueryData(["admin-domain"], fresh);
      setEditing(false);
      toast.success("Public domain updated");
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Update failed"),
  });

  if (isLoading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 animate-pulse text-slate-400 text-[13px]" data-testid="your-domain-banner-loading">
        Loading domain configuration…
      </div>
    );
  }

  const source = data?.source || "default";
  const sourceStyle = {
    db_override: "bg-brand-blue/10 text-brand-blue border-brand-blue/30",
    env:         "bg-slate-100 text-slate-600 border-slate-200",
    default:     "bg-amber-50 text-amber-700 border-amber-200",
  }[source];
  const sourceLabel = {
    db_override: "custom · DB",
    env:         "from env",
    default:     "default",
  }[source];

  return (
    <div
      className="rounded-xl border border-brand-blue/20 bg-brand-blue/5 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4"
      data-testid="your-domain-banner"
    >
      <div className="flex items-center gap-3 shrink-0">
        <div className="w-10 h-10 rounded-lg bg-white border border-brand-blue/30 flex items-center justify-center">
          <Globe size={18} weight="bold" className="text-brand-blue" />
        </div>
        <div className="leading-tight">
          <div className="text-[10px] uppercase tracking-[0.24em] text-brand-blue font-mono font-semibold">
            Your Domain
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Used in every outbound email, PDF footer, and OG tag
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        {!editing ? (
          <div className="flex items-center gap-3">
            <a
              href={data?.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-slate-900 font-mono text-[14px] font-medium truncate hover:text-brand-blue transition-colors"
              data-testid="your-domain-value"
            >
              <LinkSimple size={13} className="shrink-0" />
              <span className="truncate">{data?.url}</span>
            </a>
            <span
              className={`inline-flex items-center h-6 px-2 rounded-full border text-[10px] uppercase tracking-widest font-mono font-semibold ${sourceStyle}`}
              data-testid="your-domain-source"
              title={
                source === "db_override" ? "Overridden by super-admin — takes precedence over env"
                : source === "env"        ? "Set via PUBLIC_APP_URL environment variable"
                :                           "No override configured — using platform default"
              }
            >
              {sourceLabel}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 w-full">
            <Input
              type="url"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save.mutate(draft);
                if (e.key === "Escape") { setEditing(false); setDraft(data?.url || ""); }
              }}
              placeholder="https://sres-srproduct.com"
              className="h-10 bg-white border-slate-300 text-[14px] font-mono focus-visible:border-brand-blue focus-visible:ring-brand-blue"
              data-testid="your-domain-input"
            />
            <Button
              size="sm"
              onClick={() => save.mutate(draft)}
              disabled={save.isPending || draft === data?.url}
              className="h-10 bg-brand-blue hover:bg-brand-blueDeep text-white"
              data-testid="your-domain-save"
            >
              <Check size={14} weight="bold" className="mr-1.5" /> Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setEditing(false); setDraft(data?.url || ""); }}
              className="h-10 border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="your-domain-cancel"
            >
              <X size={14} weight="bold" />
            </Button>
          </div>
        )}
      </div>

      {!editing && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setEditing(true)}
          className="shrink-0 h-10 border-brand-blue/30 text-brand-blue hover:bg-brand-blue hover:text-white"
          data-testid="your-domain-edit"
        >
          <PencilSimple size={14} weight="bold" className="mr-1.5" /> Change
        </Button>
      )}

      {source === "default" && !editing && (
        <div
          className="w-full md:w-auto md:ml-2 inline-flex items-start gap-2 text-[11px] text-amber-700 max-w-xs leading-snug"
          data-testid="your-domain-tip"
        >
          <Info size={13} weight="bold" className="shrink-0 mt-0.5" />
          <span>No override set — using the platform default. Point your DNS to activate the professional domain.</span>
        </div>
      )}
    </div>
  );
}
